import { cn } from '@/lib/utils';

export type BrandVariant = 'full' | 'stacked' | 'inline' | 'mark';
export type BrandSize = 'sm' | 'md' | 'lg' | 'xl';
export type BrandTone = 'default' | 'inverse' | 'muted';

export interface BrandWordmarkProps {
  variant?: BrandVariant;
  size?: BrandSize;
  tone?: BrandTone;
  className?: string;
}

interface SizePair {
  suite: string;
  product: string;
  gap: string;
  markBox: string;
  markSuite: string;
  markProduct: string;
}

// Ratio suite:product ≈ 0.55.
// Arbitrary JIT values are already used elsewhere in the codebase, so no new
// classes are added to tailwind.config.
const SIZE_MAP: Record<BrandSize, SizePair> = {
  sm: {
    suite: 'text-[10px] leading-none',
    product: 'text-[18px] leading-none',
    gap: 'gap-1.5',
    markBox: 'h-6 w-6',
    markSuite: 'text-[8px] leading-none',
    markProduct: 'text-[16px] leading-none',
  },
  md: {
    suite: 'text-[13px] leading-none',
    product: 'text-[24px] leading-none',
    gap: 'gap-2',
    markBox: 'h-8 w-8',
    markSuite: 'text-[10px] leading-none',
    markProduct: 'text-[20px] leading-none',
  },
  lg: {
    suite: 'text-[16px] leading-none',
    product: 'text-[30px] leading-none',
    gap: 'gap-2.5',
    markBox: 'h-10 w-10',
    markSuite: 'text-[12px] leading-none',
    markProduct: 'text-[24px] leading-none',
  },
  xl: {
    suite: 'text-[20px] leading-none',
    product: 'text-[38px] leading-none',
    gap: 'gap-3',
    markBox: 'h-14 w-14',
    markSuite: 'text-[16px] leading-none',
    markProduct: 'text-[32px] leading-none',
  },
};

interface ToneClasses {
  suite: string;
  product: string;
  markBg: string;
  markBorder: string;
}

const TONE_MAP: Record<BrandTone, ToneClasses> = {
  default: {
    suite: 'text-sand-600',
    product: 'text-ocean-700',
    markBg: 'bg-sand-50',
    markBorder: 'border-sand-200',
  },
  inverse: {
    suite: 'text-sand-300',
    product: 'text-sand-50',
    markBg: 'bg-ocean-800',
    markBorder: 'border-ocean-700',
  },
  muted: {
    suite: 'text-sand-500',
    product: 'text-sand-700',
    markBg: 'bg-sand-100',
    markBorder: 'border-sand-200',
  },
};

function SuiteName({ className }: { className?: string }) {
  return (
    <span
      className={cn('font-sans font-light lowercase tracking-[0.08em]', className)}
      aria-hidden="true"
    >
      shaka
    </span>
  );
}

function ProductName({ className }: { className?: string }) {
  return (
    <span
      className={cn('font-serif font-semibold tracking-tight', className)}
      aria-hidden="true"
    >
      Deck
    </span>
  );
}

export function BrandWordmark({
  variant = 'full',
  size = 'md',
  tone = 'default',
  className,
}: BrandWordmarkProps) {
  const sizes = SIZE_MAP[size];
  const tones = TONE_MAP[tone];
  const label = 'shaka Deck';

  if (variant === 'mark') {
    return (
      <div
        className={cn(
          'inline-flex shrink-0 items-baseline justify-center rounded-md border',
          sizes.markBox,
          tones.markBg,
          tones.markBorder,
          className,
        )}
        role="img"
        aria-label={label}
      >
        <SuiteName className={cn(sizes.markSuite, tones.suite)} />
        <ProductName className={cn(sizes.markProduct, tones.product)} />
      </div>
    );
  }

  if (variant === 'stacked') {
    return (
      <div
        className={cn('inline-flex flex-col items-start', className)}
        role="img"
        aria-label={label}
      >
        <SuiteName className={cn(sizes.suite, tones.suite, 'mb-1')} />
        <ProductName className={cn(sizes.product, tones.product)} />
      </div>
    );
  }

  if (variant === 'inline') {
    // Both names share the same base size for compact contexts.
    return (
      <span
        className={cn('inline-flex items-baseline gap-1', className)}
        role="img"
        aria-label={label}
      >
        <SuiteName className={cn(sizes.suite, tones.suite)} />
        <ProductName className={cn(sizes.suite, tones.product, 'font-serif font-semibold')} />
      </span>
    );
  }

  // variant === 'full'
  return (
    <div
      className={cn('inline-flex items-baseline', sizes.gap, className)}
      role="img"
      aria-label={label}
    >
      <SuiteName className={cn(sizes.suite, tones.suite)} />
      <ProductName className={cn(sizes.product, tones.product)} />
    </div>
  );
}

export default BrandWordmark;
